"use client";

import { ChevronDown, HelpCircle, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Faq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    question: "What does your free property inspection include?",
    answer: "We inspect your home’s exterior perimeter, foundation sill plates, crawlspace access points, and common pest entryways. We identify active pests or vulnerabilities and discuss our findings with you clearly, without any sales pressure."
  }, {
    question: "How do your quarterly protection plans work?",
    answer: "Our quarterly protection plans start at $99 per quarter to establish and maintain an exterior barrier against common pests like ants, spiders, and roaches. Every three months, our technicians reinforce this defense before seasonal shifts push insects toward your living quarters."
  }, {
    question: "Are your pest treatments safe for children and pets?",
    answer: "We prioritize responsible applications using eco-friendly options applied primarily to exterior foundations and targeted crevices. Our technicians discuss the specific products being used and outline any short-term precautions, such as letting treated surfaces dry before pets re-enter the area."
  }, {
    question: "What service areas across North Carolina do you cover?",
    answer: "Attic to Basement Pest Control is based in Durham, NC. We provide dedicated service throughout the Triangle, including Durham, Raleigh, Cary, Chapel Hill, Wake Forest, Morrisville, and Hillsborough."
  }, {
    question: "How can I schedule service or request a quote?",
    answer: "You can schedule service anytime by calling us directly at 919-896-1953 or by submitting our simple online request form on the Contact page. We will discuss your pest concerns and arrange a convenient inspection visit."
  }];
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(faq => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer
      }
    }))
  };
  return <section data-section-id="services-faq" id="services-faq" className="bg-background text-foreground py-20 md:py-32 border-b border-border">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} />
      <div className="container mx-auto px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <div ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                <Badge variant="outline" className="mb-4 border-primary/30 text-primary bg-primary/5">
                  Frequently Asked Questions
                </Badge>
                <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold leading-[1.05] tracking-tight text-foreground mb-6">
                  Frequently Asked Questions
                </h2>
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-8 max-w-md">
                  Helpful answers regarding our perimeter treatments, quarterly protection plans, and free property inspections.
                </p>

                <Button asChild size="lg" variant="outline" className="border-primary text-foreground hover:bg-primary hover:text-primary-foreground rounded-md">
                  <Link href="tel:9198961953">
                    <Phone className="size-4 text-primary mr-2" />
                    Call 919-896-1953
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="w-full space-y-4">
              {faqs.map((faq, i) => <details key={i} data-index={i} open className="group border border-border/60 bg-card rounded-md px-4 sm:px-6 shadow-sm open:border-primary/40 open:shadow-md transition-all duration-200">
                  <summary className="list-none [&::-webkit-details-marker]:hidden cursor-pointer text-base sm:text-lg font-default font-semibold hover:text-primary transition-colors py-5 text-left flex items-center justify-between">
                    <div className="flex items-center gap-4 text-left pr-4">
                      <div className="shrink-0 flex items-center justify-center size-10 rounded-md bg-primary/10 text-primary">
                        <HelpCircle className="size-5" />
                      </div>
                      <h3>{faq.question}</h3>
                    </div>
                    <ChevronDown className="size-4 shrink-0 text-muted-foreground transition-transform duration-200 group-open:rotate-180" />
                  </summary>
                  <div className="text-muted-foreground leading-relaxed pb-6 pt-2 sm:pl-14 font-sans text-sm sm:text-base border-t border-border/40">
                    <p>{faq.answer}</p>
                  </div>
                </details>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}